// The icon of a message notification in a browser (web mode). A browser draws whatever the avatar
// URL decodes to, so a placeholder photo — anonymised profiles store a 1x1 PNG, served as a valid
// image — shows up as a blank grey tile. The in-app <Avatar> has the same problem and measures the
// bitmap, falling back to the person's initials; this does the same for notifications: a real
// photo stays, anything else becomes the sender's initials on their avatar hue, drawn to a PNG data
// URL (browsers accept data: icons). No name to draw from → '' and the notification shows the logo.

import { probeAvatarUsability } from '@/lib/images/avatar-probe';
import { getAvatarFillColor } from '@/lib/messages/avatar-color';
import { getThreadInitials } from '@/lib/messages/messages-helpers';

const ICON_SIZE_PX = 192;
// Never hold a notification hostage to a slow avatar endpoint. An unfinished probe keeps running
// and caches its answer for the sender's next message.
const PROBE_TIMEOUT_MS = 2000;
const MAX_CACHED_ICONS = 100;
const FONT_STACK = 'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** @type {Map<string, string>} `${initials}|${hex}` -> PNG data URL */
const iconCache = new Map();

/**
 * The thread list's initials rule (first + last name initial, see getThreadInitials), but only
 * when the result has a letter — a "name" that is really a phone number would otherwise draw "+4".
 * @param {string} name
 * @returns {string} '' when there is nothing worth drawing.
 */
export function getNotificationInitials(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) {
    return '';
  }
  const initials = getThreadInitials(trimmed);
  return /\p{L}/u.test(initials) ? initials : '';
}

function renderInitialsIcon(initials, fill) {
  const key = `${initials}|${fill.hex}`;
  const cached = iconCache.get(key);
  if (cached) {
    return cached;
  }
  if (typeof document === 'undefined') {
    return '';
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = ICON_SIZE_PX;
    canvas.height = ICON_SIZE_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return '';
    }
    const half = ICON_SIZE_PX / 2;
    ctx.beginPath();
    ctx.arc(half, half, half, 0, Math.PI * 2);
    ctx.fillStyle = fill.hex;
    // A canvas that can't parse oklch() ignores this assignment and keeps the hex.
    ctx.fillStyle = fill.css;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${Math.round(ICON_SIZE_PX * (initials.length > 1 ? 0.4 : 0.46))}px ${FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Center the glyphs' ink, not the font's line box (which would sit them visibly high).
    const metrics = ctx.measureText(initials);
    const ascent = Number(metrics.actualBoundingBoxAscent) || 0;
    const descent = Number(metrics.actualBoundingBoxDescent) || 0;
    ctx.fillText(initials, half, half + (ascent - descent) / 2);
    const dataUrl = canvas.toDataURL('image/png');
    iconCache.set(key, dataUrl);
    if (iconCache.size > MAX_CACHED_ICONS) {
      iconCache.delete(iconCache.keys().next().value);
    }
    return dataUrl;
  } catch (_error) {
    return '';
  }
}

function isUsablePhoto(url) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    probeAvatarUsability(url).then(
      (usable) => {
        clearTimeout(timer);
        resolve(usable);
      },
      () => {
        clearTimeout(timer);
        resolve(false);
      },
    );
  });
}

/**
 * @param {{ avatarUrl?: string, name?: string }} sender `name` must be a person's name (never a raw
 *   id) — it seeds both the initials and the hue, like the thread list's avatar.
 * @returns {Promise<string>} the avatar URL when it is a real photo, else an initials PNG data URL,
 *   else '' (the caller's fallback icon applies).
 */
export async function resolveNotificationIcon({ avatarUrl = '', name = '' } = {}) {
  const url = String(avatarUrl || '').trim();
  if (url && typeof window !== 'undefined' && (await isUsablePhoto(url))) {
    return url;
  }
  const initials = getNotificationInitials(name);
  const fill = getAvatarFillColor(name);
  if (!initials || !fill) {
    return '';
  }
  return renderInitialsIcon(initials, fill);
}
